import React, { createContext, useContext, useEffect, useState } from 'react';
import { onIdTokenChanged, signOut as fbSignOut, User } from 'firebase/auth';
import { auth } from '../firebase';

type AuthState = {
  user: User | null;
  idToken: string | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState>({
  user: null,
  idToken: null,
  loading: true,
  signOut: async () => {},
});

type Props = { children: React.ReactNode };

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fires on sign-in, sign-out, AND token refresh (Firebase refreshes
    // every ~55 minutes), so we always hold a non-stale token.
    return onIdTokenChanged(auth, async (u) => {
      if (u) {
        const token = await u.getIdToken();
        setUser(u);
        setIdToken(token);
      } else {
        setUser(null);
        setIdToken(null);
      }
      setLoading(false);
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, idToken, loading, signOut: () => fbSignOut(auth) }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
